import { useQuery } from '@tanstack/react-query'

import { fetchWithHeaders } from 'utils'

import { GrapeAPIError, type GrapeAPIErrorType } from './GrapeAPIError'
import { type MoneyAmount } from '../../types'

export type ConsistentDeduction = {
  id: string
  amount: MoneyAmount
  allowance_amount: MoneyAmount
  total_premiums: MoneyAmount
  employer_contribution?: MoneyAmount
  correction?: MoneyAmount
  expected_span_amount?: MoneyAmount
  pay_periods_in_span?: number
  expected_ytd_amount?: MoneyAmount
  pretax: boolean
  status: string
  created_at: string
  span_start_date: string | null
  span_end_date: string | null
  pay_period?: {
    id: string
    pay_date: string
    end_date: string
    status: string
    pay_schedule?: {
      id: string
      name: string
      frequency: string
      start_date: string
    }
  }
  expected_annual_amount?: MoneyAmount
  payroll_correction?: {
    id: string
    amount: MoneyAmount
  }
  benefit_enrollment?: {
    id: string
    state: string
  }
  reported_deduction?: {
    id: string
    ytd_amount: MoneyAmount
    through_date: string
  }
}

export type GroupedConsistentDeduction = {
  payPeriodId: string
  payDate: string
  payScheduleName: string
  pretaxDeduction?: ConsistentDeduction
  posttaxDeduction?: ConsistentDeduction
}

export type ConsistentDeductionResponse = {
  data: ConsistentDeduction[]
}

export function useGetMemberConsistentDeductions (memberId: string, selectedYear: number) {
  return useQuery<ConsistentDeductionResponse, GrapeAPIErrorType, GroupedConsistentDeduction[]>({
    queryKey: [
      'admin',
      'member',
      memberId,
      'consistent_deductions',
      selectedYear
    ],
    queryFn: async () => {
      const response = await fetchWithHeaders(
        `/api/web/admin/member_consistent_deductions?member_id=${memberId}&year=${selectedYear}`
      )
      if (!response.ok) {
        const errorJson = await response.json()
        throw GrapeAPIError({
          status: response.status,
          message: errorJson.message,
          errors: errorJson.errors,
        })
      }
      return response.json()
    },
    select: (data) => {
      // Group deductions by pay period
      const deductionsByPayPeriod = new Map<string, GroupedConsistentDeduction>()

      data.data.forEach(deduction => {
        if (!deduction.pay_period) return

        const payPeriodId = deduction.pay_period.id

        if (!deductionsByPayPeriod.has(payPeriodId)) {
          deductionsByPayPeriod.set(payPeriodId, {
            payPeriodId,
            payDate: deduction.pay_period.pay_date,
            payScheduleName: deduction.pay_period.pay_schedule?.name || '',
            pretaxDeduction: deduction.pretax ? deduction : undefined,
            posttaxDeduction: !deduction.pretax ? deduction : undefined
          })
        } else {
          const existingGroup = deductionsByPayPeriod.get(payPeriodId)!
          if (deduction.pretax) {
            existingGroup.pretaxDeduction = deduction
          } else {
            existingGroup.posttaxDeduction = deduction
          }
        }
      })

      // Convert map to array and sort by pay date (newest first)
      return Array.from(deductionsByPayPeriod.values())
        .sort((a, b) => new Date(b.payDate).getTime() - new Date(a.payDate).getTime())
    }
  })
}
